import React, { useMemo } from "react";
import StarIcon from "../../assets/svg/star-icon";
import VerbachatIcon from "../../assets/svg/verbachat-icon";
import VerbamailIcon from "../../assets/svg/verbamail-icon";
import VerbacallIcon from "../../assets/svg/verbacall-icon";
import VerbamessengerIcon from "../../assets/svg/verbamessenger-icon";
import { Avatar } from "../Avatar/Avatar";
import { getDateTimeByFormat } from "../../redux/slices/dateTimeFormatSlice";
import { useSearchHighlight } from "../../context/SearchHighlightContext";
import { highlightTextInPlain } from "../../pages/Communications/utils/highlightSearchText";

type Modality = "email" | "call" | "messenger" | "chat";

interface FeedbackMessageBlockProps {
  modality: Modality;
  rating: number;
  feedbackMessage: string;
  timestamp?: string;
  isSent?: boolean;
  iconType?: "verbaflo" | "participant" | "agent";
  participantInfo?: {
    text: string;
    size?: number;
    showOnlineStatus?: boolean;
    isOnline?: boolean;
    fullName?: string;
  };
  messageId?: string;
}

const FeedbackMessageBlock: React.FC<FeedbackMessageBlockProps> = ({
  modality,
  rating,
  feedbackMessage,
  timestamp = "8:00 PM",
  iconType,
  isSent,
  participantInfo,
  messageId,
}) => {
  const { searchQuery } = useSearchHighlight();

  const highlightedFeedbackMessage = useMemo(() => {
    if (!feedbackMessage) return "";
    if (searchQuery && searchQuery.trim() !== "") {
      return highlightTextInPlain(feedbackMessage, searchQuery);
    }
    return feedbackMessage;
  }, [feedbackMessage, searchQuery]);

  const modalityConfig = {
    email: {
      icon: VerbamailIcon,
      label: "VerbaMail",
      color: "text-verba-mail dark:text-verba-mail-dk",
    },
    call: {
      icon: VerbacallIcon,
      label: "VerbaCall",
      color: "text-verba-lavender dark:text-verba-lavender-dk",
    },
    messenger: {
      icon: VerbamessengerIcon,
      label: "VerbaMessenger",
      color: "text-verba-messenger dark:text-verba-messenger-dk",
    },
    chat: {
      icon: VerbachatIcon,
      label: "VerbaChat",
      color: "text-verba-chat dark:text-verba-chat-dk",
    },
  };

  const { icon: ModalityIcon, label, color } = modalityConfig[modality];

  const renderStars = () => {
    const stars = [];
    const maxStars = 5;

    for (let i = 1; i <= maxStars; i++) {
      const isFilled = i <= rating;
      stars.push(
        <StarIcon
          key={i}
          className={`w-[14px] h-[14px] ${
            isFilled
              ? "text-rating-yellow dark:text-rating-yellow-dk"
              : "text-border-gray dark:text-border-gray/25"
          }`}
        />
      );
    }

    return stars;
  };

  const renderIcon = () => {
    if (!iconType) return null;

    return (
      <>
        <div className="w-[2px] h-[2px] bg-gray-placeholder dark:bg-gray-placeholder-dk rounded-full"></div>
        {iconType === "participant" && participantInfo && (
          <div
            className={`flex items-center gap-2 ${
              isSent ? "flex-row-reverse" : "flex-row"
            }`}
          >
            <div className="shrink-0 relative">
              <Avatar
                size={participantInfo.size || 24}
                text={participantInfo.text}
                className="shrink-0"
              />
              {participantInfo.showOnlineStatus && (
                <div className="w-[6px] h-[6px] bg-pure-white dark:bg-pure-white-dk rounded-full absolute bottom-0 right-0 flex flex-col items-center justify-center">
                  <div
                    className={`w-[4px] h-[4px] ${
                      participantInfo.isOnline
                        ? "bg-verba-green-success dark:bg-verba-green-success-dk"
                        : "bg-offline dark:bg-offline-dk"
                    } rounded-full`}
                  ></div>
                </div>
              )}
            </div>
            {participantInfo.fullName && (
              <span className="text-sm truncate max-w-28 font-medium text-heading-text dark:text-heading-text-dk">
                {participantInfo.fullName}
              </span>
            )}
          </div>
        )}
      </>
    );
  };

  return (
    <div
      className={`w-[500px] flex flex-col gap-[4px]  ${
        isSent ? "ml-auto" : "mr-auto"
      }`}
      data-message-id={messageId}
    >
      <div className="flex flex-col gap-[6px] bg-feedback-card-bg dark:bg-feedback-card-bg-dk border border-feedback-card-border dark:border-feedback-card-border-dk rounded-t-[8px] rounded-br-[8px] px-[12px] py-[10px] message-card">
        <div className="flex items-center gap-[4px]">
          <div className="w-[16px] h-[16px] flex flex-col items-center justify-center rounded-full border border-rating-star-border dark:border-rating-star-border-dk">
            <StarIcon className="w-[9px] h-[9px] text-rating-yellow dark:text-rating-yellow-dk" />
          </div>
          <p className="font-medium text-md text-heading-text dark:text-heading-text-dk">
            Feedback Received
          </p>
        </div>
        <div className="flex items-center gap-[6px]">
          <div className="flex items-center gap-[2px]">{renderStars()}</div>
          <div className="text-heading-base dark:text-heading-text-dk text-base font-semibold">
            <p>
              Rated <span>{rating}</span> / 5
            </p>
          </div>
        </div>
        <div className="text-md text-heading-text dark:text-heading-text-dk">
          <p dangerouslySetInnerHTML={{ __html: highlightedFeedbackMessage }} />
        </div>
      </div>

      <div
        className={`flex items-center justify-end gap-[4px] ${
          isSent ? "flex-row" : "flex-row-reverse"
        }`}
      >
        <div className="flex items-center gap-[2px]">
          <ModalityIcon className={`w-[14px] h-[14px] ${color}`} />
          {/* <p className="text-sm font-medium text-heading-text dark:text-heading-text-dk">
            {label}
          </p> */}
        </div>
        <div className="w-[2px] h-[2px] bg-gray-placeholder dark:bg-gray-placeholder-dk rounded-full"></div>
        <div>
          <p className="text-sm font-medium text-gray-placeholder dark:text-gray-placeholder-dk">
            {getDateTimeByFormat(new Date(timestamp || "")).time}
          </p>
        </div>
        {iconType && renderIcon()}
      </div>
    </div>
  );
};

export default FeedbackMessageBlock;
